import { BottomNav } from '@/components/layout/bottom-nav'
import { ResumePreviewPanel } from '@/components/resume-preview-panel'
import { useAccessControl } from '@/hooks/use-access-control'
import { useAuth } from '@/hooks/use-auth'
import { subscribe, useEventStream } from '@/hooks/use-event-stream'
import { useParticipantWorkflowRefresh } from '@/hooks/use-participant-workflow-refresh'
import { documentService } from '@/services/document.service'
import { useChatStore } from '@/stores/chat-store'
import { getUserDisplayName } from '@/types'
import { LogOut, Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

export function MobileAppLayout() {
  useEventStream()
  const location = useLocation()
  const isResumeRoute = location.pathname === '/resume' || location.pathname.startsWith('/resume/')
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const authorization = useAccessControl()
  useParticipantWorkflowRefresh(
    authorization.data?.participant?.cooling_off_ends_at,
    authorization.refetch,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const {
    previewPanelOpen,
    resumeContent,
    originalResumeContent,
    selectedTemplate,
    userDocumentId,
    isGeneratingRevision,
    isUpdatingPreview,
    previewJobTitle,
    previewCompany,
    previewPdfUrl,
    previewIsAgentTailored,
    previewLoading,
    setSelectedTemplate,
    setIsGeneratingRevision,
    setPreviewPanelOpen,
    clearResumePreviewContext,
  } = useChatStore()

  const displayName = getUserDisplayName(user)
  const displayInitial = displayName[0]?.toUpperCase() || 'U'

  // Close menu on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on pathname change
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // On mobile, prevent stale coach-triggered preview overlays from carrying across tabs.
  // Resume-page preview should be opened explicitly via the eye icon.
  useEffect(() => {
    if (!location.pathname.startsWith('/chat')) {
      setPreviewPanelOpen(false)
    }
  }, [location.pathname, setPreviewPanelOpen])

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  // Hide bottom nav when virtual keyboard is open
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const handleResize = () => {
      // If visual viewport is significantly smaller than window height, keyboard is open
      const threshold = window.innerHeight * 0.75
      setKeyboardOpen(vv.height < threshold)
    }

    vv.addEventListener('resize', handleResize)
    return () => vv.removeEventListener('resize', handleResize)
  }, [])

  const handleGenerateFromPreview = async () => {
    if (!userDocumentId || previewIsAgentTailored) return

    setIsGeneratingRevision(true)
    try {
      const revision = await documentService.generateRevision(userDocumentId, {
        template_id: selectedTemplate,
        use_draft: true,
        trigger: 'button',
      })

      let finalStatus: string = revision.status
      if (finalStatus === 'compiling') {
        finalStatus = await new Promise<string>((resolve) => {
          const timeout = setTimeout(() => resolve('timeout'), 60000)
          const unsubscribe = subscribe((event) => {
            if (event.type !== 'revision_status_changed') return
            const data = event.data as { revision_id: number; status: string }
            if (data.revision_id === revision.id) {
              clearTimeout(timeout)
              unsubscribe()
              resolve(data.status)
            }
          })
        })
      }

      if (finalStatus === 'ready') {
        const result = await documentService.getRevisionDownloadUrl(
          userDocumentId,
          revision.id,
          'pdf',
        )
        const openedWindow = window.open(result.download_url, '_blank')
        if (!openedWindow) {
          const link = document.createElement('a')
          link.href = result.download_url
          link.download = result.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } else if (finalStatus === 'failed') {
        alert('Failed to generate resume. Please try again.')
      } else if (finalStatus === 'timeout') {
        alert('Resume generation is taking longer than expected. Please try again.')
      }
    } catch (error) {
      console.error('Failed to generate revision from preview:', error)
      alert('Failed to generate resume. Please try again.')
    } finally {
      setIsGeneratingRevision(false)
    }
  }

  const [isDownloadingPreview, setIsDownloadingPreview] = useState(false)

  const handleDownloadFromPreview = async () => {
    if (!userDocumentId) return

    setIsDownloadingPreview(true)
    try {
      const { download_url } = await documentService.getOriginalDownloadUrl(userDocumentId)
      window.open(download_url, '_blank')
    } catch (error) {
      console.error('Failed to download tailored resume:', error)
      alert('Failed to download resume. Please try again.')
    } finally {
      setIsDownloadingPreview(false)
    }
  }

  return (
    <div className="flex flex-col h-dvh">
      {/* Header */}
      <header className="h-12 flex items-center px-4 border-b bg-background shrink-0">
        <Link
          to="/home"
          aria-label="Home"
          className="flex items-center gap-1.5 text-lg font-bold tracking-tight"
        >
          Pelgo
          <span className="text-xs font-medium text-muted-foreground">Home</span>
        </Link>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="p-1.5 rounded-lg hover:bg-muted"
        >
          <Menu className="h-5 w-5" />
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-hidden relative">
        <div key={location.pathname} className="h-full overflow-y-auto animate-slide-up">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav - hidden when keyboard is open */}
      {!keyboardOpen && <BottomNav />}

      {/* Hamburger menu overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setMenuOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setMenuOpen(false)}
        />
      )}

      {/* Hamburger menu drawer */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-64 bg-background flex flex-col transition-transform duration-200 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-12 flex items-center justify-between px-4 border-b">
          <span className="text-sm font-semibold">Menu</span>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-status-100 dark:bg-status-800/50 flex items-center justify-center text-sm font-medium text-status-600 shrink-0">
              {displayInitial}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{displayName}</p>
              {user?.email && displayName !== user.email?.split('@')[0] && (
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1" />

        <div className="px-4 py-3 border-t">
          <div className="flex items-center justify-center text-[10px] text-muted-foreground">
            <a
              href="https://pelgo.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground/80 transition-colors"
            >
              Terms of Use
            </a>
            <span className="mx-2 select-none text-muted-foreground" aria-hidden="true">
              •
            </span>
            <a
              href="https://pelgo.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground/80 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>

        {/* Logout */}
        <div className="border-t px-4 py-3">
          <button
            type="button"
            onClick={async () => {
              setMenuOpen(false)
              try {
                await logout()
                navigate('/login', { replace: true })
              } catch (error) {
                console.error('Logout failed:', error)
              }
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 hover:bg-muted rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      {isResumeRoute && previewPanelOpen && userDocumentId !== null && (
        <div className="fixed inset-0 z-[60] bg-background">
          <ResumePreviewPanel
            content={resumeContent}
            originalContent={originalResumeContent}
            template={selectedTemplate}
            onTemplateChange={setSelectedTemplate}
            onGenerate={handleGenerateFromPreview}
            onDownload={previewIsAgentTailored ? handleDownloadFromPreview : undefined}
            onClose={() => {
              setPreviewPanelOpen(false)
              clearResumePreviewContext()
            }}
            isGenerating={isGeneratingRevision}
            isUpdatingContent={isUpdatingPreview}
            isLoading={previewLoading}
            isAgentTailored={previewIsAgentTailored}
            isDownloading={isDownloadingPreview}
            pdfUrl={previewPdfUrl}
            jobTitle={previewJobTitle}
            company={previewCompany}
          />
        </div>
      )}
    </div>
  )
}
